/**
 * The queue: a list of videos to play that lives in this tab and nowhere else.
 *
 * It is deliberately **not** a playlist. Nothing on the instance stores it, nothing syncs it, and no
 * endpoint is involved in any operation in this file — which is why a queue can do the two things a
 * playlist here cannot: its rows can really change places (the API has no reorder endpoint,
 * `playlist/manage.ts`) and it can hold the same video twice. Both fall out of the same fact, that
 * the list is ours.
 *
 * **A row is addressed by its `key`, never by its position.** A queue can hold one video several
 * times, so a `videoId` does not name a row; and every edit here renumbers, so an index does not name
 * one either. The key is minted from a counter carried in the state, which is what keeps it unique
 * for the queue's whole life without this file reaching for a clock or a random number — both of
 * which would make every function below untestable and neither of which core is willing to hold.
 *
 * **The cursor follows the entry, not the index.** Every edit recomputes it by looking up where the
 * playing row *ended up*, so a reorder that moves rows past the playing one cannot silently change
 * what is playing. The one operation that cannot do this is removing the playing row itself, and
 * that one has a rule of its own — see {@link queueRemove}.
 *
 * **A queue can nonetheless be a window on a playlist.** Opening one builds the queue out of it and
 * keeps the two linked ({@link QueueLink}): the rows the reader is looking at are that playlist's
 * rows, so an edit to them is an edit to the playlist and the list fills as the reader moves through
 * it. What a linked queue gives up is what the API cannot do — there is no reorder endpoint, so the
 * two acts that move rows are not offered — and {@link queueActs} is the single place that question is
 * answered. {@link queueUnlink} is how a reader takes the list for their own instead, and
 * {@link queueVideosToSave} is the other direction: which of a list of their own's videos a playlist
 * has still to be given.
 */

/**
 * The playlist a queue is a window on.
 *
 * Enough of the playlist's header to draw the panel with nothing fetched, plus the one thing an edit
 * has to ask before it is offered.
 */
export interface QueueLink {
  readonly playlistId: string;
  /** The playlist's own title, so a panel can name it with nothing fetched. */
  readonly title: string | null;
  /** From the playlist header. `null` for a list that declares no count (a YouTube mix carries none). */
  readonly videoCount: number | null;
  /** Whether this build can write to it: an `IVPL…` list this account owns, with a session to write with. */
  readonly editable: boolean;
}

/** One row. Everything a panel row draws, so adding to the queue costs no request (`listing/model.ts`). */
export interface QueueEntry {
  /**
   * Unique for the life of the queue — `videoId` is not, because a queue may hold the same video
   * twice, and a position is not, because every edit here renumbers.
   *
   * A row of the queue's own is keyed from `seq` and reads `q…`; a row of a linked playlist is keyed
   * from its own `playlistIndex` and reads `p…`. The second is the point of the link rather than a
   * detail of it: the loader re-projects the whole queue every time a page of the playlist arrives, and
   * a key minted from the row's identity makes that projection *the same rows* rather than new ones —
   * so a reconciliation and a cursor that follows by key both hold across a fetch.
   */
  readonly key: string;
  readonly videoId: string;
  readonly title: string;
  /** `null` where the record carried no channel — a collaboration credit, or an unscraped row. */
  readonly author: string | null;
  /** The channel's page, already a route. `null` when there is no single channel to point at. */
  readonly authorHref: string | null;
  readonly thumbnailUrl: string | null;
  readonly durationSec: number | null;
  /**
   * The playlist entry's removal key while the queue is linked, and `null` for a row of the queue's
   * own. §6: removal keys on `indexId` and a YouTube playlist's rows carry none at all, so a linked
   * row can have a `playlistIndex` and still no way to be taken out.
   */
  readonly indexId: string | null;
  /** The row's 0-based `index` in the linked playlist. `null` for a row of the queue's own. */
  readonly playlistIndex: number | null;
}

/**
 * A video on its way into the queue: an entry that has not been given its row key yet.
 *
 * The two linked fields are left out rather than nulled, because a video handed to {@link queueAppend}
 * or {@link queueStart} is one the reader picked off a card and is a row of the queue's own by
 * construction. A row that belongs to a playlist arrives as a {@link QueueLinkedRow}.
 */
export type QueueVideo = Omit<QueueEntry, 'key' | 'indexId' | 'playlistIndex'>;

/** One row of a linked playlist on its way in: a video, its position in that list and its removal key. */
export type QueueLinkedRow = QueueVideo & {
  readonly indexId: string | null;
  readonly playlistIndex: number;
};

export interface QueueState {
  readonly entries: readonly QueueEntry[];
  /**
   * Which row the player is on, or `null` when the queue holds a list that nothing is playing from.
   *
   * The `null` arm is a real state rather than a way of saying "empty", and both of its producers are
   * **endings**: {@link queueAdvance} past the last row, and {@link queueRemove} taking the playing
   * last row out. Neither throws the list away — the panel still shows it and a press on any row
   * starts it again — so "the list is over" and "there is no list" have to stay tellable apart.
   */
  readonly cursor: number | null;
  /** The next row key's number. In the state so a key stays unique across a reload. */
  readonly seq: number;
  /**
   * The playlist this queue is a window on, or `null` for a list of the reader's own.
   *
   * A linked queue holds a contiguous stretch of that playlist rather than all of it, so the rows grow
   * as the reader reads on. Which acts the link takes away is {@link queueActs}.
   */
  readonly link: QueueLink | null;
}

export const EMPTY_QUEUE: QueueState = { entries: [], cursor: null, seq: 0, link: null };

export function queueIsEmpty(state: QueueState): boolean {
  return state.entries.length === 0;
}

/** The row the player is on. `null` while the queue is not driving anything. */
export function queueCurrent(state: QueueState): QueueEntry | null {
  if (state.cursor === null) return null;
  return state.entries[state.cursor] ?? null;
}

/**
 * A row with nothing behind it to play: a **linked** row the endpoint blanked.
 *
 * A playlist entry whose video is gone comes back with every field emptied rather than being left out
 * (`playlist/entries.ts`, on 6.9 % of all rows measured), and the row is kept so that the numbering
 * stays truthful. So an empty title is what says the video is gone, and it can only say it about a row
 * that came from a playlist — a row the reader put in the queue off a card arrived with its title.
 */
export function queueEntryGone(state: QueueState, entry: QueueEntry): boolean {
  return state.link !== null && entry.title.trim() === '';
}

/**
 * The rows either side of the cursor — the transport's two steps, and what an ending plays next.
 *
 * **Neither wraps**: reaching the end of a list is an ending, and looping back to the first row is a
 * decision nobody made.
 *
 * **A blanked row is stepped over** ({@link queueEntryGone}), because there is nothing behind it to
 * play and the reader would otherwise be handed a dead row every twenty entries of a long playlist,
 * with the list stopping there each time. That is the opposite call from a video the *instance* refuses
 * once it is asked, which stays where it is and says so: that one is not knowable until it has been
 * tried, while this row says what it is in the payload it arrived in.
 */
export function queueNeighbours(state: QueueState): {
  readonly previous: QueueEntry | null;
  readonly next: QueueEntry | null;
} {
  if (state.cursor === null) return { previous: null, next: null };
  return {
    previous: playableFrom(state, state.cursor, -1),
    next: playableFrom(state, state.cursor, 1),
  };
}

/** Which edits this queue can be asked for. */
export interface QueueActs {
  /** Whether the rows can really change places — {@link queueReverse} and {@link queueMove}. */
  readonly reorder: boolean;
  /** Whether a video can be put next in line — {@link queuePlayNext}. */
  readonly playNext: boolean;
  /** Whether a video can be added at all. */
  readonly add: boolean;
  /** Whether a row can be taken out. */
  readonly remove: boolean;
}

/**
 * What may be done to this queue.
 *
 * One tested place for the question rather than four controls each deciding it, and it is shared with
 * every platform that draws the list.
 *
 * A queue of the reader's own answers yes to all four: nothing on the instance stores it, so there is
 * nobody to refuse. A **linked** one is a playlist, and two of the four stop existing there — this API
 * has no reorder endpoint (`playlist/manage.ts`), so neither turning the list around nor pulling a row
 * up to play next is an act a playlist has at all. The other two follow the link's own editability: an
 * `IVPL…` list this account owns can be written to, and any other list is a read-only mirror.
 */
export function queueActs(state: QueueState): QueueActs {
  const link = state.link;
  if (link === null) return { reorder: true, playNext: true, add: true, remove: true };
  return { reorder: false, playNext: false, add: link.editable, remove: link.editable };
}

/* ------------------------------------------------------------------------------------------------
 * The edits
 * ---------------------------------------------------------------------------------------------- */

/**
 * Start a queue over: these videos in this order, playing the first one.
 *
 * The shape "add to queue with nothing playing" takes. `seq` carries forward rather than resetting,
 * so a key from the discarded list can never collide with one from the new list inside a single
 * React reconciliation.
 *
 * **Any link goes with the old list.** Every row here is one the reader picked off a card, so a link
 * kept across this would name a playlist the queue holds none of — and the mirroring would then send
 * those rows to it.
 */
export function queueStart(state: QueueState, videos: readonly QueueVideo[]): QueueState {
  const { entries, seq } = mint(state.seq, videos);
  return { entries, cursor: entries.length === 0 ? null : 0, seq, link: null };
}

/**
 * Add at the end. What is playing does not change.
 *
 * The rows are the queue's own — a `q…` key, neither linked field set — while any link stands, so a
 * linked queue can end up holding a row its playlist does not. Sending the addition on to the playlist
 * is the caller's, since it is a request and this file makes none.
 */
export function queueAppend(state: QueueState, videos: readonly QueueVideo[]): QueueState {
  const { entries, seq } = mint(state.seq, videos);
  return { ...state, entries: [...state.entries, ...entries], seq };
}

/**
 * Add at the end, and start the list again when it had nothing playing.
 *
 * The shape an **import** takes (`queue/handover.ts`): the videos another tab handed over go where
 * every other addition goes, and a queue that had already played itself out then moves on to the
 * first of them rather than sitting at its ending with new rows underneath it. A queue with a cursor
 * is untouched by the second half, so this is {@link queueAppend} exactly wherever something is
 * playing.
 *
 * The cursor lands on the first row that arrived rather than on row 0, because a played-out queue
 * keeps its list: the rows in front are the ones the reader has already watched.
 */
export function queueAppendAndPlay(
  state: QueueState,
  videos: readonly QueueVideo[],
): QueueState {
  const next = queueAppend(state, videos);
  if (next.cursor !== null || videos.length === 0) return next;
  return { ...next, cursor: state.entries.length };
}

/**
 * Add directly after the playing row — "next in line".
 *
 * With nothing playing they go to the **front**, which is the same sentence about a queue that is
 * not running: next in line is the next thing this queue would play, and that is entry 1.
 *
 * The rows are the queue's own, exactly as {@link queueAppend}'s are.
 */
export function queueInsertNext(state: QueueState, videos: readonly QueueVideo[]): QueueState {
  const { entries, seq } = mint(state.seq, videos);
  const at = state.cursor === null ? 0 : state.cursor + 1;
  const next = [...state.entries.slice(0, at), ...entries, ...state.entries.slice(at)];
  // The cursor sits *before* the insertion point in both arms, so it cannot move — but it is
  // recomputed rather than assumed, because that is the invariant this whole file holds to.
  return { ...state, entries: next, cursor: trackCursor(state, next), seq };
}

/**
 * Put this video **next in line**, wherever it is now — the Play next answer a card offers once a
 * queue exists.
 *
 * A video the queue already holds is **moved** rather than queued a second time. The reader is
 * looking at one card and saying "this one, next"; answering with a second copy of it three rows
 * further down leaves the queue holding the same video twice on a press that never mentioned adding.
 * With two copies already in the list the one **nearest the cursor** moves, the same rule
 * {@link queueFollowVideo} settles a duplicate with, because it is the same question: which of these
 * identical rows is the one the reader means.
 *
 * The video that is **already playing** is left exactly as it is. "Next" is a position it is one
 * past, so there is nothing to do, and moving it would take the picture off the screen.
 */
export function queuePlayNext(state: QueueState, video: QueueVideo): QueueState {
  return nextUp(state, video);
}

/**
 * Take a row out.
 *
 * **Removing the playing row advances the queue.** A queue is a list of what to play next, so taking
 * the playing entry out of it is answered by playing the next one — the cursor stays at its index,
 * which is now the successor's. That is the one place this differs from the playlist panel, where
 * removing the playing entry deliberately keeps it playing (`screens/watch/model.ts`): a playlist is
 * a collection the reader is watching *through* and the video is not in question, while a queue is
 * the question. It also leaves no hole: the queue's row is the corner player's whole target, so a
 * cursor pointing at a row that no longer exists would be a player with nothing to play.
 *
 * Removing the **last** row while it is playing stops the queue (`cursor: null`) rather than
 * wrapping to the front.
 *
 * A link survives a removal — the rest of the rows are still that playlist's — but not the removal
 * that empties the list, since a queue with no rows is a window on nothing.
 */
export function queueRemove(state: QueueState, key: string): QueueState {
  const at = state.entries.findIndex((entry) => entry.key === key);
  if (at === -1) return state;
  const entries = state.entries.filter((entry) => entry.key !== key);
  if (entries.length === 0) return { ...EMPTY_QUEUE, seq: state.seq };
  if (at !== state.cursor) return { ...state, entries, cursor: trackCursor(state, entries) };
  return { ...state, entries, cursor: at < entries.length ? at : null };
}

/**
 * Does the queue hold this video at all? What a card's own button asks before it decides whether it
 * is an Add or a Remove, and the one queue question whose subject is a **video** rather than a row.
 */
export function queueHoldsVideo(state: QueueState, videoId: string): boolean {
  return state.entries.some((entry) => entry.videoId === videoId);
}

/**
 * Take **every** row of this video out — the card button's Remove, which is offered because the video
 * is in the queue and must therefore leave it.
 *
 * Every copy rather than one, and that is the whole reason this is not {@link queueRemove}. A card in
 * a grid names a *video*; it has no row in mind and cannot show which of two identical rows it would
 * have taken. Removing one and leaving another would answer "remove this from the queue" with a queue
 * that still holds it, and the button would then still say Remove after doing what it said.
 *
 * The cursor follows the same rules a row-by-row removal would, because that is what this is: each
 * copy is handed to {@link queueRemove}, so removing the playing one advances exactly as it does
 * there. Taking one row of two identical ones out is the panel's job, since its rows carry keys.
 */
export function queueRemoveVideo(state: QueueState, videoId: string): QueueState {
  let next = state;
  for (const entry of state.entries) {
    if (entry.videoId === videoId) next = queueRemove(next, entry.key);
  }
  return next;
}

/**
 * Move a row. `to` is the index it should occupy **after** the move, in the list as it stands, which
 * is the only spelling that stays right for a move *downwards*: taking the row out shifts everything
 * below it up by one, so the two readings of "index 2" are different slots.
 *
 * What this is for is {@link queuePlayNext}, which is a move to the row after the cursor.
 *
 * A link is carried through, because a move is not offered on a linked queue in the first place:
 * {@link queueActs} answers `reorder: false` there, and a caller that asks anyway gets rows in an order
 * the playlist does not have rather than a refusal this file cannot report.
 */
export function queueMove(state: QueueState, key: string, to: number): QueueState {
  const from = state.entries.findIndex((entry) => entry.key === key);
  if (from === -1) return state;
  const target = clamp(to, 0, state.entries.length - 1);
  if (target === from) return state;
  const rest = state.entries.filter((_, index) => index !== from);
  const moved = state.entries[from];
  if (moved === undefined) return state;
  const entries = [...rest.slice(0, target), moved, ...rest.slice(target)];
  return { ...state, entries, cursor: trackCursor(state, entries) };
}

/**
 * Turn the whole list around: last row first, first row last.
 *
 * A **real** reorder, unlike the playlist panel's flip, which only changes what is drawn because there
 * is no reorder endpoint to call. So there is no "reversed" flag anywhere afterwards and nothing to
 * remember: the list simply is in this order now, and a second reverse puts it back because reversing
 * twice is the identity. A stored flag would also start lying at the next edit, since a video appended
 * to a flipped queue is at its end and not at its start.
 *
 * **The cursor follows the playing row** by key, this file's invariant, so a reverse moves the whole
 * list past whatever is playing without changing what that is. No key is minted, so `seq` stands.
 *
 * A list with nothing to reverse answers with the **same state** rather than an equal copy, the
 * identity contract {@link queuePlay} and {@link queueMove} keep: the store skips a write whose result
 * is identical, which is what keeps a press that changes nothing from re-rendering every subscriber
 * and rewriting `sessionStorage`.
 *
 * A link is carried through for {@link queueMove}'s reason: the control is not offered on a linked
 * queue, since a playlist has no reorder to send.
 */
export function queueReverse(state: QueueState): QueueState {
  if (state.entries.length < 2) return state;
  const entries = [...state.entries].reverse();
  return { ...state, entries, cursor: trackCursor(state, entries) };
}

/**
 * Play this row. The queue's own way of jumping about, and what a press on a row means.
 *
 * A row that is already the playing one answers with the **same state**, not an equal copy: the
 * store skips a write whose result is identical (`queue/store.ts`), so identity here is what keeps a
 * press on the playing row from re-rendering every subscriber and rewriting `sessionStorage`.
 */
export function queuePlay(state: QueueState, key: string): QueueState {
  const at = state.entries.findIndex((entry) => entry.key === key);
  if (at === -1 || at === state.cursor) return state;
  return { ...state, cursor: at };
}

/**
 * Play the next row — what the end of a video means while the queue is driving.
 *
 * At the end of the list the queue **stops** rather than clearing: the reader still has their list,
 * and a queue that deleted itself on reaching its end would leave nothing to go back into.
 */
export function queueAdvance(state: QueueState): QueueState {
  if (state.cursor === null) return state;
  // The same step {@link queueNeighbours} names, so what the transport's forward button points at and
  // what an ending plays cannot come apart — blanked rows included, which both step over.
  const next = playableIndexFrom(state, state.cursor, 1);
  return { ...state, cursor: next };
}

/**
 * Throw the list away. The panel's Clear and the corner player's Close, which are the only two acts
 * that empty a queue outright.
 *
 * **The link goes with the rows.** Nothing is sent: this empties the reader's window on the playlist,
 * which is not the same act as emptying the playlist, and the way to keep the rows while letting go of
 * the playlist is {@link queueUnlink}.
 */
export function queueClear(state: QueueState): QueueState {
  return queueIsEmpty(state) ? state : { ...EMPTY_QUEUE, seq: state.seq };
}

/**
 * Put the cursor on this video, if the queue holds it.
 *
 * **The watch route is the truth while the reader is on it.** Arriving at `/watch?v=X` — by a press
 * on a queue row, by auto-advance, by the back button or by a pasted link — is what makes X the
 * queue's current entry. A video the queue does not hold leaves the cursor exactly where it was,
 * which is this function declining rather than a policy: the app clears the whole queue on that
 * arrival instead (`queue/useQueueAdvance.ts`), so a caller here only ever asks about a video the
 * queue holds.
 *
 * With the same video queued twice, the occurrence **nearest the cursor** wins, and the tie goes
 * backwards so that "the one I am already on" beats "the one after it". A press on a row does not
 * depend on this — it sets the cursor itself before the navigation — but a reload of that row's URL
 * does, and nearest is the only answer that keeps a duplicate from throwing the reader to the front.
 */
export function queueFollowVideo(state: QueueState, videoId: string): QueueState {
  if (queueCurrent(state)?.videoId === videoId) return state;
  const best = nearestRow(state, videoId);
  return best === -1 ? state : { ...state, cursor: best };
}

/* ------------------------------------------------------------------------------------------------
 * The link — a queue built out of a playlist, the way back out of one, and the way into one
 * ---------------------------------------------------------------------------------------------- */

/** The stretch of a playlist the queue is to become, and the row the route is asking for. */
export interface QueuePlaylistWindow {
  readonly link: QueueLink;
  /** A contiguous run of the playlist, in the playlist's own order. */
  readonly rows: readonly QueueLinkedRow[];
  /** The `index` the route names, or `null` when it names none. */
  readonly currentIndex: number | null;
  /** The video the route names, or `null` when it names none. */
  readonly currentVideoId: string | null;
}

/**
 * The queue becomes this window on that playlist — what opening `/watch?list=X` means.
 *
 * Called again for every page that arrives, so it is a **projection** rather than an append: the rows
 * are re-derived from the run in hand each time, and their `p…` keys make the result the same rows the
 * reader was already looking at, only more of them. `seq` carries forward untouched, since a linked row
 * mints no key from it.
 *
 * **The cursor goes on the row the route names.** The video first, because that is what is playing: the
 * row whose `videoId` matches, and the one nearest `currentIndex` when the run holds it more than once.
 * Failing that the row *at* `currentIndex`, which is what a route carrying a position but no matching
 * video is asking for.
 *
 * **A run holding neither leaves the queue alone**, a decline in {@link queueFollowVideo}'s idiom
 * rather than a best effort. Entries standing with `cursor: null` is the queue's *ended* state and it
 * draws the ended box in the corner, so a projection that cannot place the cursor would tell the reader
 * a list they just opened is over. The caller's answer is to ask for the page holding the index, which
 * is `playlistWalk`'s anchored walk.
 */
export function queueFromPlaylist(state: QueueState, from: QueuePlaylistWindow): QueueState {
  const cursor = placeCursor(from.rows, from.currentVideoId, from.currentIndex);
  if (cursor === null) return state;
  const entries = from.rows.map((row) => ({ ...row, key: `p${row.playlistIndex}` }));
  return { entries, cursor, seq: state.seq, link: from.link };
}

/**
 * Let go of the playlist and keep the rows: the queue becomes a list of the reader's own.
 *
 * **Whatever is loaded is what they keep.** Nothing is fetched to complete the list first, because the
 * act is "this is mine now" and a queue that went away to read 70 pages before answering it would be
 * doing something the reader did not ask for.
 *
 * Every row loses both linked fields and is re-keyed from `seq`, so the keys read `q…` again. That is
 * not tidiness: the keys are what say a row belongs to a playlist, and a row still carrying a `p…` key
 * would be re-projected away by the next page to arrive. The rows keep their places, so the cursor
 * keeps pointing at the same row.
 */
export function queueUnlink(state: QueueState): QueueState {
  if (state.link === null && !state.entries.some(isLinkedRow)) return state;
  return { ...state, ...remint(state.entries, state.seq), link: null };
}

/**
 * Which of this queue's videos a playlist has still to be given — the ids to send when a list of the
 * reader's own is saved as a playlist, in the queue's own order.
 *
 * `held` is what that playlist already holds. Whatever the caller can answer that with is the caller's
 * business: the owned-playlists response embeds up to 100 entries per playlist, which settles it for
 * free on a short one, and a longer one has to be read first. A wrong answer is not a refusal but a
 * duplicate, since the endpoint accepts the same video twice without complaint (`playlist/manage.ts`).
 *
 * **Each video once, however many times the queue holds it.** A queue can hold the same video twice —
 * it is one of the two things this list can do that a playlist here cannot — and a playlist gaining it
 * twice out of one press is not what "save this list" means.
 *
 * **A row with nothing behind it is left out.** An empty title is a playlist entry the endpoint blanked
 * ({@link queueEntryGone} reads the same field while the link stands), and those ids are videos YouTube
 * has removed: the add would be refused per row, so one of them in a queue of thirty would stop the save
 * partway for a row that was never watchable.
 */
export function queueVideosToSave(
  state: QueueState,
  held: ReadonlySet<string>,
): readonly string[] {
  const send: string[] = [];
  const seen = new Set(held);
  for (const entry of state.entries) {
    if (entry.videoId === '' || entry.title.trim() === '' || seen.has(entry.videoId)) continue;
    seen.add(entry.videoId);
    send.push(entry.videoId);
  }
  return send;
}

/* ------------------------------------------------------------------------------------------------
 * Storage
 * ---------------------------------------------------------------------------------------------- */

/**
 * Bumped when the stored shape changes. A queue is worth less than a cache — it is one tab's list of
 * what to watch next — so a version that does not match is dropped rather than migrated.
 */
export const QUEUE_VERSION = 2;

export interface StoredQueue {
  readonly version: number;
  readonly entries: readonly QueueEntry[];
  readonly cursor: number | null;
  readonly seq: number;
  readonly link: QueueLink | null;
}

/**
 * Validate what came out of storage. R4's division of labour: `JSON.parse` happens in the platform
 * file, and core decides whether the parsed value is a queue.
 *
 * Anything unrecognised answers an empty queue rather than throwing, because the only caller is a
 * boot path: a blob some other version wrote must cost the reader their queue, not their app. Rows
 * are validated individually and a bad one is **dropped** rather than poisoning the list — a queue is
 * a list of independent rows, unlike a chapter list, where a broken line means the whole parse was
 * reading something that is not a chapter list at all (`playback/…`).
 *
 * The **link is the one field that fails smaller than the queue**: a link nobody can read drops the
 * link and keeps the rows as a list of the reader's own ({@link queueUnlink}), because those rows are
 * still the videos they were watching while the playlist behind them is only where they came from.
 */
export function readStoredQueue(value: unknown): QueueState {
  if (!isRecord(value) || value['version'] !== QUEUE_VERSION) return EMPTY_QUEUE;
  const raw = value['entries'];
  if (!Array.isArray(raw)) return EMPTY_QUEUE;

  const entries: QueueEntry[] = [];
  const keys = new Set<string>();
  for (const item of raw) {
    const entry = readEntry(item);
    // A duplicate key would collide in React's reconciliation, and two rows the edits cannot tell
    // apart is worse than one row lost.
    if (entry === null || keys.has(entry.key)) continue;
    keys.add(entry.key);
    entries.push(entry);
  }
  if (entries.length === 0) return EMPTY_QUEUE;

  const storedCursor = value['cursor'];
  const cursor =
    typeof storedCursor === 'number' && Number.isInteger(storedCursor)
      ? clamp(storedCursor, 0, entries.length - 1)
      : null;
  const storedSeq = value['seq'];
  // The counter must clear every key already in the list, whatever the file claimed — a `seq` that
  // rewinds mints a key that already exists. A linked row's key is a playlist index rather than a
  // counter reading, so it says nothing about where the counter is and must not raise it.
  const floor = entries.reduce((high, entry) => Math.max(high, keyNumber(entry.key) + 1), 0);
  const seq =
    typeof storedSeq === 'number' && Number.isInteger(storedSeq) && storedSeq >= floor
      ? storedSeq
      : floor;

  const link = readLink(value['link']);
  const state = { entries, cursor, seq, link };
  return link === null ? queueUnlink(state) : state;
}

/**
 * Validate a list of videos that arrived from **outside this tab** — the offer another Visious tab
 * makes when its list is being taken over (`queue/handover.ts`).
 *
 * R4's division of labour, with a different carrier: the platform owns the message and the `JSON` in
 * it, core decides whether what came back is a list of videos. A row that does not read as one is
 * **dropped** rather than failing the list, {@link readStoredQueue}'s rule and for its reason — these
 * are independent rows, and losing one of eleven videos is not losing the import.
 *
 * No key and neither linked field is read, because neither means anything here: a row arriving from
 * another tab is a video this queue is about to mint its own row for, and the other tab's key would
 * collide with one of ours.
 */
export function readQueueVideos(value: unknown): readonly QueueVideo[] {
  if (!Array.isArray(value)) return [];
  const videos: QueueVideo[] = [];
  for (const item of value) {
    const video = readVideo(item);
    if (video !== null) videos.push(video);
  }
  return videos;
}

/** What goes into storage. Spelled out rather than spread, so a field added here is a decision. */
export function toStoredQueue(state: QueueState): StoredQueue {
  return {
    version: QUEUE_VERSION,
    entries: state.entries,
    cursor: state.cursor,
    seq: state.seq,
    link: state.link,
  };
}

/* ------------------------------------------------------------------------------------------------
 * Internals
 * ---------------------------------------------------------------------------------------------- */

function mint(
  seq: number,
  videos: readonly QueueVideo[],
): { readonly entries: readonly QueueEntry[]; readonly seq: number } {
  const entries = videos.map((video, offset) => ({
    ...video,
    key: `q${seq + offset}`,
    indexId: null,
    playlistIndex: null,
  }));
  return { entries, seq: seq + videos.length };
}

/** Rows that were a playlist's, made the queue's own: a fresh `q…` key each and neither linked field. */
function remint(
  entries: readonly QueueEntry[],
  seq: number,
): { readonly entries: readonly QueueEntry[]; readonly seq: number } {
  return {
    entries: entries.map((entry, offset) => ({
      ...entry,
      key: `q${seq + offset}`,
      indexId: null,
      playlistIndex: null,
    })),
    seq: seq + entries.length,
  };
}

function isLinkedRow(entry: QueueEntry): boolean {
  return entry.indexId !== null || entry.playlistIndex !== null;
}

/**
 * Which row of a playlist window the route is asking for, or `null` when the window holds none of them.
 *
 * The video decides it wherever the window holds one, since that is what is playing; the position only
 * settles which of several copies is meant, and stands alone for a route that names a position the
 * window has no matching video at. Nearest wins with the tie going backwards, {@link nearestRow}'s rule
 * for the same question.
 */
function placeCursor(
  rows: readonly QueueLinkedRow[],
  videoId: string | null,
  index: number | null,
): number | null {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  if (videoId !== null) {
    for (const [at, row] of rows.entries()) {
      if (row.videoId !== videoId) continue;
      const distance = index === null ? at : Math.abs(row.playlistIndex - index);
      if (distance < bestDistance) {
        best = at;
        bestDistance = distance;
      }
    }
  }
  if (best === -1 && index !== null) {
    best = rows.findIndex((row) => row.playlistIndex === index);
  }
  return best === -1 ? null : best;
}

/**
 * Which row of this video the reader means: the one **nearest the cursor**, tie going backwards, and
 * `-1` when the queue does not hold it at all. Read from index 0 when nothing is playing.
 *
 * Every question whose subject is a video rather than a row goes through here, so a queue holding
 * the same video twice answers them all the same way — "the one I am already on" beats "the one
 * after it", and neither the follow nor the move can throw the reader to the front of the list.
 */
function nearestRow(state: QueueState, videoId: string): number {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  const from = state.cursor ?? 0;
  for (const [index, entry] of state.entries.entries()) {
    if (entry.videoId !== videoId) continue;
    const distance = Math.abs(index - from);
    if (distance < bestDistance) {
      best = index;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * The queue with this video next in line, whether it has to be inserted or only moved.
 *
 * The move's target index is stated in the list **as it will be**, which is what {@link queueMove}
 * takes: a row lifted from *before* the playing one pulls it up a slot, so "directly after the
 * playing row" is `cursor` there and `cursor + 1` from the other side. Getting that wrong is
 * off-by-one in one direction only, which is the shape of bug that survives a hand check.
 */
function nextUp(state: QueueState, video: QueueVideo): QueueState {
  const at = nearestRow(state, video.videoId);
  const held = state.entries[at];
  if (at === -1 || held === undefined) return queueInsertNext(state, [video]);
  if (at === state.cursor) return state;
  const to = state.cursor === null ? 0 : at < state.cursor ? state.cursor : state.cursor + 1;
  return queueMove(state, held.key, to);
}

/**
 * The first row this side of `from` that has something to play, as an index, or `null` at that end of
 * the list. `step` is `1` forwards and `-1` back.
 */
function playableIndexFrom(state: QueueState, from: number, step: number): number | null {
  for (let at = from + step; at >= 0 && at < state.entries.length; at += step) {
    const entry = state.entries[at];
    if (entry !== undefined && !queueEntryGone(state, entry)) return at;
  }
  return null;
}

/** The same row as itself rather than as an index — what {@link queueNeighbours} hands back. */
function playableFrom(state: QueueState, from: number, step: number): QueueEntry | null {
  const at = playableIndexFrom(state, from, step);
  return at === null ? null : (state.entries[at] ?? null);
}

/** Where the playing row ended up, by key. `null` when nothing was playing. */
function trackCursor(state: QueueState, entries: readonly QueueEntry[]): number | null {
  const key = queueCurrent(state)?.key;
  if (key === undefined) return null;
  const at = entries.findIndex((entry) => entry.key === key);
  return at === -1 ? null : at;
}

/** What the counter had reached when this key was minted, and `-1` for a key it did not mint. */
function keyNumber(key: string): number {
  const parsed = Number.parseInt(key.slice(1), 10);
  return key.startsWith('q') && Number.isInteger(parsed) ? parsed : -1;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readEntry(value: unknown): QueueEntry | null {
  if (!isRecord(value)) return null;
  const key = value['key'];
  if (typeof key !== 'string' || key === '') return null;
  const video = readVideo(value);
  if (video === null) return null;
  return {
    ...video,
    key,
    // Each on its own, like the optional three inside {@link readVideo}: a row whose removal key is
    // unreadable is still a row the reader can watch, and it loses only the act that needed the key.
    indexId: optionalString(value['indexId']),
    playlistIndex: optionalIndex(value['playlistIndex']),
  };
}

/**
 * The video half of a row: the two fields without which there is nothing to play or to draw, and the
 * four that fail on their own.
 *
 * Shared by the stored row above and by {@link readQueueVideos}, because they are the same question
 * asked of two carriers — this tab's storage and another tab's message — and a second copy of it
 * would be the place the two drift apart.
 */
function readVideo(value: unknown): QueueVideo | null {
  if (!isRecord(value)) return null;
  const videoId = value['videoId'];
  const title = value['title'];
  if (typeof videoId !== 'string' || videoId === '') return null;
  if (typeof title !== 'string') return null;
  const duration = value['durationSec'];
  return {
    videoId,
    title,
    author: optionalString(value['author']),
    authorHref: optionalString(value['authorHref']),
    thumbnailUrl: optionalString(value['thumbnailUrl']),
    durationSec: typeof duration === 'number' && Number.isFinite(duration) ? duration : null,
  };
}

/**
 * The stored link, or `null` when there is nothing readable there.
 *
 * The id is the whole of it: without one there is no playlist to name, to read or to write to, so a
 * missing or empty id is not a link however much else the record carries. `editable` has to be `true`
 * rather than merely truthy, because a value nobody can parse must not be the thing that lets an edit
 * be sent to somebody's playlist.
 */
function readLink(value: unknown): QueueLink | null {
  if (!isRecord(value)) return null;
  const playlistId = value['playlistId'];
  if (typeof playlistId !== 'string' || playlistId === '') return null;
  return {
    playlistId,
    title: optionalString(value['title']),
    videoCount: optionalIndex(value['videoCount']),
    editable: value['editable'] === true,
  };
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function optionalIndex(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}
